import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import {
  createUser,
  loadUser,
  login,
  loginGoogle,
  resetPassword,
  resetPasswordCheckToken,
  resetPasswordSetPassword,
  setPassword,
  verifyAccount,
  verifyResetAccount
} from '../actions/auth.actions'
import { catchError, filter, map, mapTo, mergeMap, switchMap, tap } from 'rxjs/operators'
import { AuthNetworkService } from '@adex/store/services/auth-network.service'
import { navigate, setStorageValue } from '@adex/store/actions/global.actions'
import { IModelUserDto } from '@adex/shared/models/dto/user.dto'
import { appRoutes } from '@adex/shared/routing/routes'
import { IEmailDto } from '@adex/shared/models/dto/email.dto'
import { ITokenDto } from '@adex/shared/models/dto/token.dto'
import { Store } from '@ngrx/store'
import { buildAlertErrorStream } from '@adex/shared/utils/errors'
import { selectLoading } from '@adex/store/selectors'
import { urls } from '@adex/shared/network/urls'
import { HttpErrorResponse } from '@angular/common/http'
import { CookieService } from 'ngx-cookie-service'


@Injectable()
export class AuthEffects {

  public readonly createUser$ = createEffect(() => this.actions$.pipe(
    ofType(createUser.type),
    map(({ userDto }: { userDto: IModelUserDto }) => ({ ...userDto, ...this.getInnerData() })),
    switchMap((userDto: IModelUserDto) => this.network.postUserCreate(userDto).pipe(
      map(() =>
        navigate({ commands: appRoutes.auth.emailConfirmation, extras: { queryParams: { email: userDto.user.login } } })),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly verifyAccount$ = createEffect(() => this.actions$.pipe(
    ofType(verifyAccount.type),
    switchMap(({ tokenDto }) => this.network.postUserConfirmEmail(tokenDto).pipe(
      mergeMap(() => [
        loadUser(),
        navigate({ commands: appRoutes.auth.setPassword })
      ]),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly verifyResetAccount$ = createEffect(() => this.actions$.pipe(
    ofType(verifyResetAccount.type),
    switchMap(({ tokenDto }) => this.network.postResetPasswordCheckToken(tokenDto).pipe(
      mapTo((tokenDto as ITokenDto).token),
      map(token =>
        navigate({ commands: appRoutes.auth.resetSetPassword, extras: { queryParams: { token } } })
      ),
      catchError(err => buildAlertErrorStream(err).pipe(
        mergeMap(alert => [ alert, navigate({ commands: appRoutes.root }) ])
      ))
    ))
  ))

  public readonly loadUser$ = createEffect(() => this.actions$.pipe(
    ofType(loadUser.type),
    switchMap(() => this.network.getUserMe().pipe(
      map((value) => setStorageValue({ key: 'user', value })),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly setPassword$ = createEffect(() => this.actions$.pipe(
    ofType(setPassword.type),
    switchMap(({ passwordDto }) => this.network.postSetPassword(passwordDto).pipe(
      mergeMap(() => [
        loadUser(),
        navigate({ commands: [ appRoutes.app.main ] })
      ]),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly resetPassword$ = createEffect(() => this.actions$.pipe(
    ofType(resetPassword.type),
    switchMap(({ emailDto }) => this.network.postResetPassword(emailDto).pipe(
      mapTo<any, string>((emailDto as IEmailDto).email),
      map(email => navigate({ commands: appRoutes.auth.emailConfirmation, extras: { queryParams: { email } } })),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly resetPasswordCheckToken$ = createEffect(() => this.actions$.pipe(
    ofType(resetPasswordCheckToken.type),
    switchMap(({ tokenDto }) => this.network.postResetPasswordCheckToken(tokenDto).pipe(
      mergeMap(() => [
        loadUser(),
        navigate({ commands: appRoutes.root })
      ]),
      catchError(err => buildAlertErrorStream(err).pipe(
        mergeMap(alert => [ alert, navigate({ commands: appRoutes.root }) ])
      ))
    ))
  ))

  public readonly resetPasswordSetPassword$ = createEffect(() => this.actions$.pipe(
    ofType(resetPasswordSetPassword.type),
    switchMap(({ resetPasswordDto }) => this.network.postResetPasswordSetPassword(resetPasswordDto).pipe(
      mergeMap(() => [
        loadUser(),
        navigate({ commands: appRoutes.app.main })
      ]),
      catchError(err => buildAlertErrorStream(err).pipe(
        mergeMap(alert => [ alert, navigate({ commands: appRoutes.root }) ])
      ))
    ))
  ))

  public readonly login$ = createEffect(() => this.actions$.pipe(
    ofType(login.type),
    switchMap(({ loginDto }) => this.network.postLogin(loginDto).pipe(
      tap(() => {
        window.localStorage.removeItem('intercomTemporaryUserId')
      }),
      mapTo(loadUser()),
      catchError(err => buildAlertErrorStream(err))
    ))
  ))

  public readonly loginGoogle$ = createEffect(() => this.actions$.pipe(
    ofType(loginGoogle.type),
    switchMap(({ data }) => this.network.loginGoogle(data).pipe(
      tap(() => {
        window.localStorage.removeItem('intercomTemporaryUserId')
      }),
      mapTo(loadUser()),
      catchError(err => buildAlertErrorStream(err).pipe(
        mergeMap(alert => [ alert, navigate({ commands: appRoutes.auth.login }) ])
      ))
    ))
  ))

  private readonly badCredentialsAlert$ = createEffect(() => this.store.select(selectLoading(urls.auth.login)).pipe(
    filter(state => state.isError),
    map(state => new HttpErrorResponse({ ...state.lastError, status: 490 })),
    switchMap(err => buildAlertErrorStream(err))
  ))

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly network: AuthNetworkService,
    private readonly cookie: CookieService
  ) {
  }

  private getInnerData(): object {
    try {
      const gaRef = this.cookie.get('_ga')
      const ga = gaRef.slice(gaRef.indexOf('.', gaRef.indexOf('.') + 1) + 1)
      const advtrk = this.cookie.get('advtrk')
      const referer = this.cookie.get('referer') || document.referrer
      return { ga, advtrk, referer }
    } catch (e) {
      return {}
    }
  }

}
